import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Keyboard } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { ImageConfig } from '../../helpers/images.helper';
import { useNavbar } from '../../contexts/navigation/NavbarContext';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import './ImagePreviewer.scss';

interface ImagePreviewerProps {
  images: ImageConfig[];
  isOpen: boolean;
  currentIndex: number;
  onClose: () => void;
  onSlideChange?: (newIndex: number) => void;
  scrollableContainerRef?: React.RefObject<HTMLElement | null>;
}

const ImagePreviewer = ({
  images,
  isOpen,
  currentIndex,
  onClose,
  onSlideChange,
  scrollableContainerRef
}: ImagePreviewerProps) => {
  const swiperRef = useRef<SwiperType | null>(null);
  const { setHideNav } = useNavbar();
  const previousOverflowRef = useRef<string>('');
  const previousContainerOverflowRef = useRef<string>('');
  const previousContainerScrollTopRef = useRef<number>(0);

  // Sync Swiper with external currentIndex changes
  useEffect(() => {
    if (swiperRef.current && isOpen) {
      swiperRef.current.slideTo(currentIndex);
    }
  }, [currentIndex, isOpen]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && swiperRef.current) {
        swiperRef.current.slidePrev();
      }
      if (e.key === 'ArrowRight' && swiperRef.current) {
        swiperRef.current.slideNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Hide navbar and prevent scroll when lightbox is open
  useEffect(() => {
    // Capture the container ref value at the time of effect execution
    const container = scrollableContainerRef?.current;

    if (isOpen) {
      // Hide navbar
      setHideNav(true);

      // Save current scroll positions and overflow states
      previousOverflowRef.current = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      // Lock scrollable container if provided
      if (container) {
        previousContainerScrollTopRef.current = container.scrollTop;
        previousContainerOverflowRef.current = container.style.overflow;
        container.style.overflow = 'hidden';
      }
    } else {
      // Show navbar
      setHideNav(false);

      // Restore body overflow
      document.body.style.overflow = previousOverflowRef.current;

      // Restore scrollable container
      if (container) {
        container.style.overflow = previousContainerOverflowRef.current;
        // Restore scroll position
        container.scrollTop = previousContainerScrollTopRef.current;
      }
    }

    return () => {
      // Cleanup: restore everything using the captured container value
      setHideNav(false);
      document.body.style.overflow = previousOverflowRef.current || '';
      if (container) {
        container.style.overflow = previousContainerOverflowRef.current || '';
      }
    };
  }, [isOpen, setHideNav, scrollableContainerRef]);

  if (!isOpen) return null;

  const handlePrevClick = () => {
    swiperRef.current?.slidePrev();
  };

  const handleNextClick = () => {
    swiperRef.current?.slideNext();
  };

  return createPortal(
    <div className="image-lightbox">
      <div className="image-lightbox__overlay" onClick={onClose} />

      <button
        className="image-lightbox__close"
        onClick={onClose}
        aria-label="Close lightbox"
      >
        <X size={32} strokeWidth={1} />
      </button>

      <div className="image-lightbox__counter">
        {currentIndex + 1} / {images.length}
      </div>

      <button
        className="image-lightbox__prev"
        onClick={handlePrevClick}
        aria-label="Previous image"
        disabled={currentIndex === 0}
      >
        <ChevronLeft size={48} strokeWidth={1} />
      </button>

      <button
        className="image-lightbox__next"
        onClick={handleNextClick}
        aria-label="Next image"
        disabled={currentIndex === images.length - 1}
      >
        <ChevronRight size={48} strokeWidth={1} />
      </button>

      <Swiper
        modules={[Navigation, Keyboard]}
        initialSlide={currentIndex}
        slidesPerView={1}
        spaceBetween={0}
        speed={300}
        allowTouchMove={true}
        onSwiper={(swiper: SwiperType) => {
          swiperRef.current = swiper;
        }}
        onSlideChange={(swiper: SwiperType) => {
          onSlideChange?.(swiper.activeIndex);
        }}
        className="image-lightbox__swiper"
      >
        {images.map((image, index) => (
          <SwiperSlide key={index}>
            <div className="image-lightbox__slide-content">
              <img
                src={image.src}
                alt={image.alt}
                draggable={false}
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>,
    document.body
  );
};

export default ImagePreviewer;
